/* eslint-disable camelcase */
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import {useRef, useCallback, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {cnb} from 'cnbuilder';
import {v4 as uuidv4} from 'uuid';
import add from 'date-fns/add';
import differenceInYears from 'date-fns/differenceInYears';
import {format} from 'date-fns-tz';
import {useI18nContext} from '@teladoc/pulse/ui/Context/i18n';
import Button from '@teladoc/pulse/ui/Button';
import DatePicker from '@teladoc/pulse/ui/DatePicker';
import Form from '@teladoc/pulse/ui/Form';
import IconInfoDefault from '@teladoc/pulse/icons/info-default.svg';
import Label from '@teladoc/pulse/ui/Label';
import Select from '@teladoc/pulse/ui/Select';
import PhoneInput from '@teladoc/pulse/ui/PhoneInput';
import Chip from '@teladoc/pulse/ui/Chip';
import RadioGroup from '@teladoc/pulse/ui/RadioGroup';
import TextArea from '@teladoc/pulse/ui/TextArea';
import Modal from '@teladoc/pulse/ui/Modal';
import Arg from '@livongo/arg';
import useMounted from '@livongo/utilities/hooks/useMounted';
import useTransLoader from 'i18n/use-trans-loader';
import AcuityError from '../ErrorMessages/AcuityError';
import SharedCoaching from '../utils/SharedCoaching';
import {
    axleTimeZones,
    defaultTimeZoneAxle,
    localizedTimeAxle,
} from '../utils/Timezone';
import {
    AXLE_SERVICES_IDS,
    DIFFERENT_COACH,
    WM_PROGRAM,
    AWM_PROGRAM,
    ADPP_PROGRAM,
    CWC_PROGRAM,
    CDPC_PROGRAM,
    ADOL_WM_PROGRAM,
    RANKED_PROGRAMS,
    ADOLESCENT_AGE_LIMIT,
    ENABLE_TRACKING_APPOINTMENT_REQUEST,
} from '../config';
import MixpanelUtils from '../common/mix-panel';
import AmpliUtils from '../utils/amplitude-utils';
import {TRACK_EVENT_KEYS} from '../constants/amplitude';
import Loader from '../common/loader/Loader';
import useApi from '../utils/use-api';
import AxleAPI from '../utils/Axle';
import css2 from './SchedulingFormV2.scss';

const languages = [
    {
        key: 'english',
        value: 'en-US',
    },
    {
        key: 'spanish',
        value: 'es-US',
    },
];
const webSessionId = crypto.randomUUID();

const SchedulingFormAxle = () => {
    const {t} = useTransLoader('schedulingForm');
    const {isMounted} = useMounted();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const contentRef = useRef();
    const {selectedCountry, selectedLang, changeLanguage} = useI18nContext();
    const {
        appointmentTypesInfo,
        firstName,
        lastName,
        email,
        phones,
        birthDate,
        pid,
        internalUserId,
    } = useSelector(state => state.userData.userData);
    const {token, uuid} = useSelector(state => state.userData.auth);
    const [selectedTimezone, setSelectedTimezone] = useState(
        defaultTimeZoneAxle(selectedCountry, selectedLang)
    );
    const [APIError, setAPIError] = useState();
    const [serviceId, setServiceId] = useState(null);
    const [hasPastSession, setHasPastSession] = useState(null);
    const [selectedDifferentCoach, setSelectedDifferentCoach] = useState(false);
    const [availableDates, setAvailableDates] = useState([]);
    const [availableTimes, setAvailableTimes] = useState([]);
    const [availableTimesForDate, setAvailableTimesForDate] = useState({});
    const [timeSlotDetails, setTimeSlotDetails] = useState({});
    const [selectedTimeSlotData, setSelectedTimeSlotData] = useState(null);
    const [selectedCoachId, setSelectedCoachId] = useState(null);
    const [disableScheduleButton, setDisableScheduleButton] = useState(false);
    const [coachId, setCoachId] = useState(null);
    const [notes, setNotes] = useState('');
    const [timeKey, setTimeKey] = useState(uuidv4());
    const [sessionKey, setSessionKey] = useState('0');
    const [showFirstVisitMsg, setShowFirstVisitMsg] = useState(false);
    const [showPastVisitMsg, setShowPastVisitMsg] = useState(false);
    const [previousSessionCoach, setPreviousSessionCoach] = useState(null);
    const [isAlreadyBooked, setIsAlreadyBooked] = useState(null);
    const [showWaitModal, setShowWaitModal] = useState(true);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [fetchedPhoneNumber, setFetchedPhoneNumber] = useState(false);
    const [previousCoachNotAvailable, setPreviousCoachNotAvailable] =
        useState(false);
    const [datePickerKey, setDatePickerKey] = useState(uuidv4());
    const isMobile = Arg('mobile') === true;
    const [previousCoach, setPreviousCoach] = useState(null);

    const onPhoneNumberChange = value => {
        MixpanelUtils.track({
            event: 'coaching.scheduling.clicked.phone',
        });

        AmpliUtils.setTrackingData(TRACK_EVENT_KEYS.PHONE_NUMBER_CHANGED, {
            [TRACK_EVENT_KEYS.PhoneNumber]: value,
        });

        setPhoneNumber(value);
    };
    const onTimezoneChange = timezone => {
        setSelectedTimezone(timezone);

        setAvailableTimes(
            availableTimes.map(timeSlot => ({
                label: localizedTimeAxle(
                    timeSlot.value,
                    timezone.value,
                    selectedLang
                ),
                value: timeSlot.value,
            }))
        );

        AmpliUtils.setTrackingData(TRACK_EVENT_KEYS.TIMEZONE_CHANGED, {
            [TRACK_EVENT_KEYS.SelectedTimeZone]: timezone?.value,
        });

        MixpanelUtils.track({
            event: 'coaching.scheduling.clicked.timezone',
        });
    };

    const processAvailabilityData = useCallback(
        availabilityData => {
            const datesSet = new Set();
            const timesForDateMap = {};
            const timeSlotProviderMap = {};

            availabilityData.providers.forEach(provider => {
                if (
                    selectedDifferentCoach &&
                    provider.enterprise_id === Number(previousSessionCoach)
                ) {
                    // Skip this provider - we're looking for a different coach and this is the same as previous session's coach
                    return;
                }

                if (
                    provider.available_slots &&
                    Array.isArray(provider.available_slots)
                ) {
                    provider.available_slots.forEach(startDateTime => {
                        const slotTime = new Date(startDateTime);
                        const now = new Date();
                        const isToday =
                            format(slotTime, 'yyyy-MM-dd') ===
                            format(now, 'yyyy-MM-dd');

                        if (isToday) {
                            const twoHoursFromNow = new Date(
                                now.getTime() + 2 * 60 * 60 * 1000
                            );

                            if (slotTime < twoHoursFromNow) {
                                // Skip this slot - not enough advance notice
                                return;
                            }
                        }
                        const dateKey = format(slotTime, 'MM/dd/yyyy');
                        const timeIdentifier = startDateTime;

                        datesSet.add(dateKey);

                        // Initialize time slot entry if it doesn't exist
                        if (!timeSlotProviderMap[timeIdentifier]) {
                            timeSlotProviderMap[timeIdentifier] = {
                                providers: [],
                                start_dt_time: startDateTime,
                            };

                            // Initialize array for this date if it doesn't exist
                            if (!timesForDateMap[dateKey]) {
                                timesForDateMap[dateKey] = [];
                            }

                            // Add time option for this date (only once per unique time)
                            timesForDateMap[dateKey].push({
                                label: localizedTimeAxle(
                                    startDateTime,
                                    selectedTimezone?.value,
                                    selectedLang
                                ),
                                value: timeIdentifier,
                            });
                        }

                        // Add provider to this time slot
                        timeSlotProviderMap[timeIdentifier].providers.push({
                            provider_id: provider.enterprise_id,
                            activeAppointmentCount:
                                provider.activeAppointmentCount,
                        });
                    });
                }
            });

            const sortedDates = Array.from(datesSet).sort((a, b) => {
                return new Date(a) - new Date(b);
            });

            Object.keys(timesForDateMap).forEach(date => {
                timesForDateMap[date].sort((a, b) => {
                    const timeA = timeSlotProviderMap[a.value]?.start_dt_time;
                    const timeB = timeSlotProviderMap[b.value]?.start_dt_time;

                    return new Date(timeA) - new Date(timeB);
                });
            });

            setAvailableDates(sortedDates);
            setAvailableTimesForDate(timesForDateMap);
            setTimeSlotDetails(timeSlotProviderMap);
        },
        [
            selectedDifferentCoach,
            previousSessionCoach,
            selectedTimezone,
            selectedLang,
        ]
    );

    const fetchChunkedAvailability = useCallback(
        async ({fetchAvailability}) => {
            const today = new Date();
            const totalDaysToFetch = 90;
            const chunkSizeInDays = 30;
            const chunkCalls = [];

            for (
                let daysElapsed = 0;
                daysElapsed < totalDaysToFetch;
                daysElapsed += chunkSizeInDays
            ) {
                const chunkStart = add(today, {days: daysElapsed});
                const chunkEnd = add(today, {
                    days: Math.min(
                        daysElapsed + chunkSizeInDays,
                        totalDaysToFetch
                    ),
                });

                chunkCalls.push(
                    fetchAvailability({
                        startDate: chunkStart.toISOString(),
                        endDate: chunkEnd.toISOString(),
                    })
                );
            }

            const results = await Promise.all(chunkCalls);

            return {
                providers: results.flatMap(result => result.providers),
            };
        },
        []
    );

    const getAllCoachesAvailability = useCallback(
        async (reasonCode, localeOverride, pastSessionOverride) => {
            if (!reasonCode) return;

            const locale = localeOverride || selectedLang;
            const hasCompletedSession = !isNil(pastSessionOverride)
                ? pastSessionOverride
                : hasPastSession;

            try {
                const allData = await fetchChunkedAvailability({
                    fetchAvailability: ({startDate, endDate}) =>
                        AxleAPI.getAllCoachesAvailability({
                            token,
                            startDate,
                            endDate,
                            reasonCode,
                            locale,
                            type: hasCompletedSession ? 'FOLLOW_UP' : 'INITIAL',
                        }),
                });

                processAvailabilityData(allData);
                setShowWaitModal(false);
            } catch (error) {
                setAPIError(error);
                setShowWaitModal(false);
            }
        },
        [
            selectedLang,
            token,
            hasPastSession,
            processAvailabilityData,
            fetchChunkedAvailability,
        ]
    );

    const getCoachAvailability = useCallback(
        async (
            reasonCode,
            overrideCoachId,
            localeOverride,
            pastSessionOverride
        ) => {
            if (!reasonCode) {
                return;
            }

            const locale = localeOverride || selectedLang;
            const hasCompletedSession = !isNil(pastSessionOverride)
                ? pastSessionOverride
                : hasPastSession;

            try {
                const allData = await fetchChunkedAvailability({
                    fetchAvailability: ({startDate, endDate}) =>
                        AxleAPI.getCoachAvailability({
                            token,
                            startDate,
                            endDate,
                            reasonCode,
                            locale,
                            type: hasCompletedSession ? 'FOLLOW_UP' : 'INITIAL',
                            providerId: overrideCoachId || selectedCoachId,
                        }),
                });

                const hasAvailableSlots = allData.providers.some(
                    provider =>
                        provider.available_slots &&
                        provider.available_slots.length > 0
                );

                // Handling edge case where a coach doesn't have any availability
                if (!hasAvailableSlots) {
                    setAvailableDates([]);
                    setAvailableTimes([]);
                    setShowWaitModal(false);
                    setPreviousCoachNotAvailable(true);

                    return;
                }

                setPreviousCoachNotAvailable(false);
                processAvailabilityData(allData);
                setShowWaitModal(false);
            } catch (error) {
                setAPIError(error);
                setShowWaitModal(false);
            }
        },
        [
            selectedCoachId,
            hasPastSession,
            selectedLang,
            token,
            processAvailabilityData,
            fetchChunkedAvailability,
        ]
    );

    const setServiceTypeId = useCallback(
        ({hasCompletedSession, providerId}) => {
            const programs = [];
            const age = differenceInYears(new Date(), new Date(birthDate));

            if (appointmentTypesInfo) {
                appointmentTypesInfo.forEach(type => {
                    switch (type.program) {
                        case ADPP_PROGRAM:
                            programs.push(ADPP_PROGRAM);
                            break;
                        case AWM_PROGRAM:
                            programs.push(AWM_PROGRAM);
                            break;
                        case CDPC_PROGRAM:
                            programs.push(CDPC_PROGRAM);
                            break;
                        case CWC_PROGRAM:
                            programs.push(CWC_PROGRAM);
                            break;
                        case WM_PROGRAM:
                            programs.push(
                                age <= ADOLESCENT_AGE_LIMIT
                                    ? ADOL_WM_PROGRAM
                                    : WM_PROGRAM
                            );
                            break;
                        default:
                            programs.push(type.program);
                            break;
                    }
                });
            }

            // https://teladocpa.sharepoint.com/:x:/s/CoachingTeamLibrary/Eb3t_Q5mDTdImG_2x3SzICIBtnU6l_ISELYoEquncE5r8w?e=m7iekB
            // i.e. If a member is enrolled in both DM and WM, they will be assigned a DM coach
            const rankedProgram = RANKED_PROGRAMS.find(program =>
                programs.includes(program)
            );

            const calculatedServiceId = AXLE_SERVICES_IDS[rankedProgram];

            setServiceId(calculatedServiceId);

            if (hasCompletedSession && !isNil(providerId)) {
                getCoachAvailability(
                    calculatedServiceId,
                    providerId,
                    null,
                    hasCompletedSession
                );
            } else {
                getAllCoachesAvailability(
                    calculatedServiceId,
                    null,
                    hasCompletedSession
                );
            }
        },
        [
            birthDate,
            appointmentTypesInfo,
            getCoachAvailability,
            getAllCoachesAvailability,
        ]
    );

    // Trigerring a re-render for the time selection so that it shows the placeholder text for the new day selected
    const renderTimeSelection = () => {
        setTimeKey(uuidv4());
        setAvailableTimes([]);
    };

    const onDaySelected = date => {
        const dateInstance = new Date(date);
        const dateKey = format(dateInstance, 'MM/dd/yyyy');

        if (coachId) {
            setCoachId(null);
        }

        if (availableTimes) {
            renderTimeSelection();
        }

        const timesForDate = availableTimesForDate[dateKey] || [];

        const formattedTimes = timesForDate.map(timeSlot => ({
            label: localizedTimeAxle(
                timeSlot.value,
                selectedTimezone?.value,
                selectedLang
            ),
            value: timeSlot.value,
        }));

        setAvailableTimes(formattedTimes);

        AmpliUtils.setTrackingData(TRACK_EVENT_KEYS.DATE_SELECTED, {
            [TRACK_EVENT_KEYS.SelectedDate]: date,
        });

        MixpanelUtils.track({
            event: 'coaching.scheduling.clicked.date',
        });
    };

    const onTimeSelected = time => {
        AmpliUtils.setTrackingData(TRACK_EVENT_KEYS.TIME_SELECTED, {
            [TRACK_EVENT_KEYS.SelectedTime]: time?.label,
            [TRACK_EVENT_KEYS.TimeValue]: time?.value,
        });

        MixpanelUtils.track({
            event: 'coaching.clicked.ontime',
            properties: {
                webSessionId,
                'initial Session': showFirstVisitMsg,
                selectedTime: time,
            },
        });

        const timeSlotInfo = timeSlotDetails[time.value];

        if (timeSlotInfo && timeSlotInfo?.providers.length > 0) {
            const selectedProvider = timeSlotInfo.providers.reduce(
                (prev, current) =>
                    prev.activeAppointmentCount < current.activeAppointmentCount
                        ? prev
                        : current
            );

            setSelectedTimeSlotData({
                provider_id: selectedProvider?.provider_id,
                start_dt_time: timeSlotInfo?.start_dt_time,
            });

            setCoachId(selectedProvider?.provider_id);
        }
    };

    const onLanguageChange = value => {
        const locale = value;

        // Resetting these values to reset the calendar and time selection until new dates are fetched for the new serviceId
        setShowWaitModal(true);
        setDatePickerKey(uuidv4());
        setAvailableDates([]);

        renderTimeSelection();

        changeLanguage(locale);

        if (hasPastSession && selectedCoachId && !selectedDifferentCoach) {
            getCoachAvailability(
                serviceId,
                selectedCoachId,
                locale,
                hasPastSession
            );
        } else {
            getAllCoachesAvailability(serviceId, locale, hasPastSession);
        }

        AmpliUtils.setTrackingData(TRACK_EVENT_KEYS.LANGUAGE_CHANGED, {
            [TRACK_EVENT_KEYS.SelectedLanguage]: locale,
        });

        MixpanelUtils.track({
            event: 'coaching.scheduling.clicked.language',
            properties: {
                selectedLagugage: locale,
            },
        });
    };

    const onDifferentCoachOptionSelected = event => {
        const id = event?.target?.id;
        const selected =
            id === t('sessionInfo.differentCoach') ? DIFFERENT_COACH : id;

        // Trigerring a re-render for the calendar so that it shows the
        // placeholder text instead of the selected day for the previous coach
        setShowWaitModal(true);
        setDatePickerKey(uuidv4());
        setAvailableDates([]);

        renderTimeSelection();

        if (selected === DIFFERENT_COACH) {
            setSelectedDifferentCoach(true);
            setSelectedCoachId(selected);
            setShowFirstVisitMsg(true);

            AmpliUtils.setTrackingData(
                TRACK_EVENT_KEYS.DIFFERENT_COACH_SELECTED
            );
        } else if (!isNil(previousCoach)) {
            setSelectedDifferentCoach(false);
            setSelectedCoachId(selected);
            setShowFirstVisitMsg(false);

            AmpliUtils.setTrackingData(TRACK_EVENT_KEYS.COACH_SELECTED, {
                [TRACK_EVENT_KEYS.CoachId]: selected,
                [TRACK_EVENT_KEYS.IsPreviousCoach]: true,
            });
        }
        MixpanelUtils.track({
            event: 'coaching.scheduling.clicked.coach',
            properties: {
                selectedCoach: selected,
            },
        });
    };

    const onScheduleAppointment = () => {
        setDisableScheduleButton(true);
        setSessionKey(uuidv4());

        AmpliUtils.setTrackingData(TRACK_EVENT_KEYS.SCHEDULE_BUTTON_CLICKED, {
            [TRACK_EVENT_KEYS.HasNotes]: notes?.trim()?.length > 0,
            [TRACK_EVENT_KEYS.IsSharedCoaching]: false,
        });

        const appointmentData = {
            member: {
                name: `${firstName} ${lastName}`,
                id: internalUserId,
                timezone_cd: selectedTimezone?.value,
                email,
                phone: phoneNumber?.startsWith('+')
                    ? phoneNumber
                    : `+1${phoneNumber}`,
                dob: format(new Date(birthDate), 'MM-dd-yyyy'),
                sex: 'U',
            },
            start_date_time: selectedTimeSlotData?.start_dt_time,
            communication_method: 'PHONE',
            reason_code: serviceId,
            appointment_type: hasPastSession ? 'FOLLOW_UP' : 'INITIAL',
            member_locale: selectedLang,
            duration_minutes: 30,
            appointment_status: 'SCHEDULED',
            provider: {
                provider_id: String(selectedTimeSlotData?.provider_id),
                system: 'CCM',
            },
            notes,
        };

        const startDateTime = new Date(selectedTimeSlotData?.start_dt_time);
        const clientStartDate = format(startDateTime, 'yyyy-MM-dd');
        const clientStartTime = format(startDateTime, 'h a');

        if (ENABLE_TRACKING_APPOINTMENT_REQUEST) {
            MixpanelUtils.track({
                event: 'coaching.scheduling.appointment.request',
                properties: {
                    webSessionId,
                    schedulingForm: 'CCM-V2',
                    reason: {
                        // internalDisplayName: appointmentData?.reason, TODO: get reason string
                        reasonId: appointmentData?.reason_code,
                    },
                    staff: {
                        providerId: appointmentData?.provider?.provider_id,
                    },
                    clientDateTime: {
                        clientStartDate,
                        clientStartTime,
                    },
                },
            });
        }

        AxleAPI.createAppointment(token, appointmentData)
            .then(data => {
                dispatch({
                    type: 'APPOINTMENT_DETAILS',
                    payload: {
                        startDate: new Date(
                            selectedTimeSlotData?.start_dt_time
                        ),
                        timezone: selectedTimezone?.value,
                        type: hasPastSession ? t('followup') : t('initial'),
                        coach: data?.provider_full_name,
                    },
                });
                dispatch({
                    type: 'VIEW',
                    payload: {
                        enableNewUi: true,
                        view: 'fullview',
                        dontShowLoader: true,
                        calendarId: data?.appointment_id,
                    },
                });

                AmpliUtils.setTrackingData(
                    TRACK_EVENT_KEYS.APPOINTMENT_SCHEDULED,
                    {
                        [TRACK_EVENT_KEYS.CalendarId]: data?.appointment_id,
                        [TRACK_EVENT_KEYS.StartDate]: data?.clientStartDate,
                        [TRACK_EVENT_KEYS.StartTime]: data?.clientStartTime,
                        [TRACK_EVENT_KEYS.Timezone]: selectedTimezone?.value,
                        [TRACK_EVENT_KEYS.CoachName]: data?.provider_full_name,
                    }
                );

                MixpanelUtils.track({
                    event: 'coaching.scheduling.success',
                    properties: {
                        enableNewUi: true,
                        clientStartDate,
                        clientStartTime,
                        staff: data?.provider_full_name,
                    },
                });

                if (ENABLE_TRACKING_APPOINTMENT_REQUEST) {
                    MixpanelUtils.track({
                        event: 'coaching.scheduling.appointment.response',
                        properties: {
                            webSessionId,
                            calendarId: data?.appointment_id,
                            startDate: clientStartDate,
                            startTime: clientStartTime,
                            timezone: selectedTimezone?.value,
                            type: hasPastSession ? t('followup') : t('initial'),
                            reasonId: serviceId,
                            coach: data?.provider_full_name,
                        },
                    });
                }
                navigate(
                    `/v2/confirmation?enableNewUi=true&view=fullview&showSession=true&calendarId=${
                        data?.appointment_id
                    }&${isMobile ? 'mobile=true' : ''}`
                );
            })
            .catch(error => {
                setAPIError(error);

                MixpanelUtils.track({
                    event: 'coaching.scheduling.failure',
                    properties: {
                        'Error details': error?.data,
                        payload: appointmentData,
                    },
                });
            });

        // Check if user has schedule a coaching session before
        SharedCoaching.getLaunchpadStatus(token)
            .then(({items}) => {
                const coachStatus = items?.find(
                    item => item.name === 'COACHING'
                );

                // User has not schedule a session
                if (!coachStatus.completed) {
                    const markItem = {
                        item: {name: 'COACHING', completed: true},
                    };

                    SharedCoaching.markLaunchpadStatus(token, markItem);
                }
            })
            .catch(err => {
                // silent
            });
        MixpanelUtils.track({
            event: 'coaching.clicked.button.submit',
        });
    };

    const onDataLoaded = useCallback(
        lastSessionData => {
            if (isMounted) {
                const appointmentsArrayLength =
                    lastSessionData?.appointments?.length;

                if (appointmentsArrayLength > 0) {
                    const appointmentData =
                        lastSessionData?.appointments[
                            appointmentsArrayLength - 1
                        ];

                    const providerId = String(
                        appointmentData?.enterprise_provider_id
                    );

                    setPreviousCoach({
                        key: providerId,
                        value: appointmentData?.provider_full_name,
                    });

                    setHasPastSession(true);
                    setPreviousSessionCoach(providerId);
                    setShowPastVisitMsg(true);
                    setSelectedCoachId(providerId);
                } else {
                    setHasPastSession(false);
                    setShowFirstVisitMsg(true);
                    setShowPastVisitMsg(false);
                    setSelectedCoachId(null);
                }
            }
        },
        [isMounted]
    );

    const {error: apiError} = useApi({
        api: AxleAPI.getLastSessionData,
        params: {
            token,
            uuid,
        },
        onLoad: onDataLoaded,
    });

    useEffect(() => {
        if (
            !isNil(appointmentTypesInfo) &&
            !isNil(hasPastSession) &&
            // If follow-up, ensure previousSessionCoach is set
            (!hasPastSession || !isNil(previousSessionCoach))
        ) {
            const providerId = hasPastSession ? previousSessionCoach : null;

            setServiceTypeId({
                hasCompletedSession: hasPastSession,
                providerId,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appointmentTypesInfo, hasPastSession, previousSessionCoach]);

    useEffect(() => {
        if (phones?.[0]?.number) {
            setPhoneNumber(phones[0].number);
            setFetchedPhoneNumber(true);
        }
    }, [phones]);

    // This is for a Follow-Up flow, will get triggred when chips are clicked
    useEffect(() => {
        if (selectedDifferentCoach || selectedCoachId === DIFFERENT_COACH) {
            getAllCoachesAvailability(serviceId, selectedLang, hasPastSession);
        } else {
            getCoachAvailability(
                serviceId,
                selectedCoachId,
                selectedLang,
                hasPastSession
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDifferentCoach]);

    useEffect(() => {
        if (APIError || apiError) {
            setShowWaitModal(false);

            if (APIError?.code?.includes('409')) {
                setIsAlreadyBooked(true);
            }

            // Track error in Amplitude
            AmpliUtils.setTrackingData(TRACK_EVENT_KEYS.SCHEDULING_ERROR, {
                [TRACK_EVENT_KEYS.ErrorCode]:
                    APIError?.code || apiError?.code || 'Unknown',
                [TRACK_EVENT_KEYS.ErrorDetails]:
                    APIError?.message || apiError?.message || 'Unknown error',
            });

            MixpanelUtils.track({
                event: 'coaching.scheduling.error',
                properties: {
                    'Error Code': APIError?.code || apiError?.code || 'Unknown',
                },
            });
        }
    }, [APIError, apiError]);

    const defaultLanguage = selectedLang;

    useEffect(() => {
        MixpanelUtils.identify(pid);
        MixpanelUtils.track({
            event: 'coaching.scheduling.viewed',
            properties: {
                schedulingForm: 'V2',
            },
        });
    }, [pid]);

    useEffect(() => {
        contentRef?.current?.focus();
    }, []);

    return (
        <div>
            <AcuityError
                isOpen={APIError}
                toggleModal={setAPIError}
                isAlreadyBooked={isAlreadyBooked}
            />
            <Modal
                isOpen={showWaitModal}
                classNameContainer={css2.modelContainer}
            >
                <Loader
                    classNameRoot={css2.loader}
                    loadingText={t('loaderInfomation.loaderText')}
                />
            </Modal>
            <div className={css2.root}>
                <Form
                    className={css2.innerFrame}
                    onSubmit={() => {
                        onScheduleAppointment();
                    }}
                >
                    <div className={css2.header}>
                        <div className={css2.logoWrapper}>
                            {!isMobile && (
                                <div className={css2.newHeader}>
                                    {t('newTitle')}
                                </div>
                            )}
                            {isNil(hasPastSession) && <Loader />}
                            {!isNil(hasPastSession) && !hasPastSession && (
                                <div className={css2.tipWrapperClear}>
                                    <span className={css2.description}>
                                        {t(
                                            'sessionInfo.firstVisitWithCoach.description'
                                        )}
                                    </span>
                                </div>
                            )}
                            <div>{t('required')}</div>
                        </div>
                        <RadioGroup
                            id="languageTag"
                            name="languageTag"
                            label={
                                <Label aria-hidden={isMobile}>
                                    {t('sessionInfo.newLanguage')}
                                </Label>
                            }
                            onChange={event =>
                                onLanguageChange(event?.target?.value)
                            }
                        >
                            {Object.keys(languages).map(language => {
                                return (
                                    <Chip
                                        key={languages[language].key}
                                        id={languages[language].key}
                                        value={String(
                                            languages[language].value
                                        )}
                                        label={
                                            languages[
                                                language
                                            ].key.toString() === 'english'
                                                ? t(`sessionInfo.english`)
                                                : t(`sessionInfo.spanish`)
                                        }
                                        defaultChecked={
                                            languages[language].value ===
                                            defaultLanguage
                                        }
                                        role="radio"
                                    />
                                );
                            })}
                        </RadioGroup>
                        {!isNil(previousCoach) && showPastVisitMsg && (
                            <RadioGroup
                                id="differentCoach"
                                name="differentCoach"
                                label={
                                    <Label aria-hidden={isMobile}>
                                        {t('sessionInfo.preferred')}
                                    </Label>
                                }
                                onChange={event =>
                                    onDifferentCoachOptionSelected(event)
                                }
                                role="radiogroup"
                            >
                                <Chip
                                    key={previousCoach.key}
                                    id={previousCoach.key}
                                    value={previousCoach.key}
                                    label={previousCoach.value}
                                    defaultChecked
                                    role="radio"
                                />
                                <Chip
                                    key={DIFFERENT_COACH}
                                    id={DIFFERENT_COACH}
                                    value={DIFFERENT_COACH}
                                    label={t('sessionInfo.differentCoach')}
                                    role="radio"
                                />
                            </RadioGroup>
                        )}
                        {hasPastSession && showFirstVisitMsg && (
                            <div className={css2.tipWrapper}>
                                <span className={css2.iconInfo}>
                                    <IconInfoDefault />
                                </span>
                                <span className={css2.description}>
                                    <span className={css2.firstTimeDesc}>
                                        {t(
                                            'sessionInfo.firstVisitWithCoach.title'
                                        )}
                                    </span>
                                    <br />
                                    {t(
                                        'sessionInfo.firstVisitWithCoach.description'
                                    )}
                                </span>
                            </div>
                        )}
                        {hasPastSession &&
                            previousCoachNotAvailable &&
                            !selectedDifferentCoach && (
                                <div className={css2.tipWrapper}>
                                    <span className={css2.iconInfo}>
                                        <IconInfoDefault />
                                    </span>
                                    <span className={css2.description}>
                                        {t('sessionInfo.coachNoAvailability')}
                                    </span>
                                </div>
                            )}
                        {fetchedPhoneNumber && (
                            <PhoneInput
                                id="phone"
                                showCountrySelect={false}
                                label={
                                    <Label aria-hidden={isMobile}>
                                        {t('phoneNumber')}
                                    </Label>
                                }
                                name="phone"
                                placeholder="(###) ###-####"
                                defaultValue={phoneNumber}
                                onChange={onPhoneNumberChange}
                                required
                            />
                        )}
                        <div className={css2.timeContainer}>
                            <DatePicker
                                key={datePickerKey}
                                placeHolder={t('selectDate')}
                                id="date"
                                name="date"
                                classNameRoot={cnb({
                                    [css2.dateTimePicker]: isMobile,
                                })}
                                classNameInput={css2.datePickerInput}
                                calendar={{
                                    disablePast: true,
                                    available: availableDates,
                                }}
                                label={
                                    <Label aria-hidden={isMobile}>
                                        {t('sessionInfo.date')}
                                    </Label>
                                }
                                disabled={isEmpty(availableDates)}
                                onChange={date => onDaySelected(date)}
                                i18nChooseLabel={t('ariaLabelCalendar')}
                                required
                            />
                        </div>
                    </div>
                    <div>
                        <Select
                            id="timeZone"
                            name="timeZone"
                            label={
                                <Label aria-hidden={isMobile}>
                                    {t('sessionInfo.timezone')}
                                </Label>
                            }
                            placeholder={t('sessionInfo.timezonePlaceholder2')}
                            items={axleTimeZones[selectedCountry].map(
                                ({value, label}) => ({
                                    value,
                                    label: t(`timeZone.${label}`),
                                })
                            )}
                            defaultValue={selectedTimezone?.value}
                            onChange={timezone => onTimezoneChange(timezone)}
                            required
                        />
                        <Select
                            key={timeKey}
                            id="time"
                            name="time"
                            disabled={isEmpty(availableTimes)}
                            label={
                                <Label aria-hidden={isMobile}>
                                    {t('sessionInfo.time')}
                                </Label>
                            }
                            items={availableTimes}
                            onChange={time => onTimeSelected(time)}
                            placeholder={t('sessionInfo.timePlaceholder')}
                            required
                        />
                    </div>
                    <TextArea
                        id="note"
                        name="note"
                        maxLength={255}
                        showCounter
                        label={<Label>{t('sessionInfo.note')}</Label>}
                        onChange={e => setNotes(e.target.value)}
                    />
                    {sessionKey !== '0' && (
                        <div
                            key={sessionKey}
                            ref={contentRef}
                            role="alert"
                            aria-live="assertive"
                            aria-atomic="true"
                            className={css2.xScreenReader}
                        >
                            {t('sessionInfo.loading')}
                        </div>
                    )}
                    <div className={css2.actions}>
                        <Button
                            key={sessionKey}
                            id="scheduleSession"
                            name="scheduleSession"
                            type="submit"
                            variant="primary"
                            className={css2.confirmButton}
                            disabled={disableScheduleButton || isNil(coachId)}
                            loading={sessionKey !== '0'}
                        >
                            {t('sessionInfo.submit')}{' '}
                        </Button>
                    </div>
                </Form>
            </div>
        </div>
    );
};

export default SchedulingFormAxle;
